import './style.scss';

type Props = {
    /** True only while operating hours are on AND the channel is closed. */
    away?: boolean;
    awayMessage?: string;
    /** AC-5.1 — the outside-hours reopen line, if the operator set one. */
    reopenLine?: string;
    /** AC-5.1 / AC-7.1 — the in-hours line, e.g. "Typically replies in a few minutes". */
    responseTimeInHours?: string;
    /** The channel's agent avatar, so the away message reads as coming from the same sender. */
    profileAvatar?: string;
    /** Sent time for the away message, formatted by the caller in the widget's own convention. */
    timestamp?: string;
};

/**
 * Inline rather than an icon-font glyph or an emoji: the widget is embedded in a stranger's page
 * and inherits whatever fonts it has, so a ⚡ or 🕐 character renders differently on every host
 * and not at all on some. currentColor keeps each locked to the text beside it.
 */
function BoltIcon() {
    return (
        <svg className="status-notice-icon" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false">
            <path d="M13 2 4.5 13.5H11l-1 8.5L18.5 10.5H12L13 2Z" fill="currentColor" />
        </svg>
    );
}

function ClockIcon() {
    return (
        <svg className="status-notice-icon" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false">
            <path
                d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm1 10.6V6h-2v7.4l5.2 3.1 1-1.7-4.2-2.2Z"
                fill="currentColor"
            />
        </svg>
    );
}

/**
 * IMBEE-8955 (T115) — what the channel says about itself.
 *
 * Closed: the away message is rendered as an agent MESSAGE — avatar, bubble and timestamp,
 * matching the approved design and reusing the widget's own .rcw-response/.rcw-avatar styling
 * rather than a panel of its own. The channel is talking to the visitor, so it should look like
 * the channel talking. Below it, centred, the reopen line with a clock.
 *
 * It is presentational, NOT a real entry in the message store. A stored message would persist,
 * duplicate on reconnect, ride the transcript, and need explicitly removing the moment the
 * channel opened. This simply stops rendering.
 *
 * Open: the in-hours response-time line, if one is set (AC-7.1). Bolt plus green text, no panel.
 *
 * Both states live in one component so they cannot both render — AC-5.1 makes the two lines
 * independent, and an operator who sets both must still never see them stacked.
 */
function StatusNotice({ away, awayMessage, reopenLine, responseTimeInHours, profileAvatar, timestamp }: Props) {
    if (away) {
        // core-php requires an away message once the feature is on, so an empty one only happens
        // for config written before that rule existed. Nothing to say, so say nothing.
        if (!awayMessage?.trim()) return null;
        return (
            <div className="status-notice status-notice--away" data-testid="status-notice" role="status">
                <div className="rcw-message status-notice-away-row">
                    {profileAvatar ? <img src={profileAvatar} className="rcw-avatar" alt="" /> : null}
                    <div className="rcw-response">
                        <div className="rcw-message-text status-notice-away-bubble">{awayMessage}</div>
                        {timestamp ? <span className="rcw-timestamp">{timestamp}</span> : null}
                    </div>
                </div>
                {reopenLine?.trim() ? (
                    <p className="status-notice-reopen">
                        <ClockIcon />
                        <span>{reopenLine}</span>
                    </p>
                ) : null}
            </div>
        );
    }

    if (!responseTimeInHours?.trim()) return null;
    return (
        <div className="status-notice status-notice--online" data-testid="status-notice" role="status">
            <BoltIcon />
            <p className="status-notice-message">{responseTimeInHours}</p>
        </div>
    );
}

export default StatusNotice;
